import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, ClipboardList, CreditCard, FileText, Flag, LayoutGrid, Link2, LogOut, Menu, ShieldCheck, SlidersHorizontal, Trash2, UserPlus, Users, X } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { Button, EmptyState, Loading } from "@/components/ui";
import { Logo } from "@/components/logo";
import { useAdminMe } from "@/components/admin-ui";
import { Lock } from "lucide-react";

export const SECTIONS = [
  { key: "members", perm: "members", title: "Members", Icon: Users, href: "/admin/members", desc: "Search, review and suspend accounts" },
  { key: "deletion", perm: "members", title: "Deletion requests", Icon: Trash2, href: "/admin/deletion-requests", desc: "Web account deletion requests" },
  { key: "reports", perm: "reports", title: "Reports", Icon: Flag, href: "/admin/reports", desc: "Member and photo reports queue" },
  { key: "verification", perm: "verification", title: "Verification", Icon: ShieldCheck, href: "/review", desc: "ID + selfie review queue" },
  { key: "payments", perm: "payments", title: "Payments", Icon: CreditCard, href: "/admin/payments", desc: "Transactions, refunds, corrections" },
  { key: "analytics", perm: "analytics", title: "Analytics", Icon: BarChart3, href: "/admin/analytics", desc: "Growth, requests and revenue" },
  { key: "groups", perm: "groups", title: "Groups", Icon: Link2, href: "/admin/groups", desc: "Community invite links & leaderboard" },
  { key: "config", perm: "config", title: "Config", Icon: SlidersHorizontal, href: "/admin/config", desc: "Fees, matching weights, missions" },
  { key: "content", perm: "content", title: "Content", Icon: FileText, href: "/admin/content", desc: "Email templates & legal pages" },
  { key: "audit", perm: "audit", title: "Audit log", Icon: ClipboardList, href: "/admin/audit", desc: "Every sensitive staff action" },
  { key: "staff", perm: "staff", title: "Staff", Icon: UserPlus, href: "/admin/staff", desc: "Create staff, edit roles" },
];

function Nav({ perms, onNavigate }) {
  const link = ({ isActive }) => `flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-foreground hover:bg-[#F6E3E4]"}`;
  return (
    <nav className="flex flex-col gap-1" aria-label="Admin">
      <NavLink end to="/admin" className={link} data-testid="admin-nav-home" onClick={onNavigate}><LayoutGrid className="h-[18px] w-[18px]" />Overview</NavLink>
      {SECTIONS.filter((s) => perms.includes(s.perm)).map((s) => (
        <NavLink key={s.key} to={s.href} className={link} data-testid={`admin-nav-${s.key}`} onClick={onNavigate}><s.Icon className="h-[18px] w-[18px]" />{s.title}</NavLink>
      ))}
    </nav>
  );
}

/** Desktop-optimised admin shell: persistent sidebar, collapsible drawer on small screens. */
export function AdminLayout() {
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { data } = useAdminMe(status === "authed" && !!user?.roles?.length);
  if (status === "loading") return <Loading />;
  if (status === "guest") return <Navigate to="/landing" replace />;
  const logout = async () => { await signOut(); navigate("/landing", { replace: true }); };
  if (user && !user.roles?.length) {
    return (
      <EmptyState testId="admin-forbidden" icon={Lock} title="Staff access only" action={
        <div className="mt-6 flex w-full flex-col gap-2">
          <Button testId="admin-forbidden-back-button" title="Back to LOVE NET" onClick={() => navigate("/", { replace: true })} />
          <Button testId="admin-forbidden-logout-button" title="Log out" variant="ghost" onClick={logout} />
        </div>
      } />
    );
  }
  const perms = data?.permissions ?? [];
  const side = (
    <div className="flex h-full flex-col p-5">
      <div className="mb-1 flex flex-col items-start gap-2"><Logo /><span className="rounded-full bg-[#F6E3E4] px-2 py-0.5 text-[11px] font-bold text-primary">Admin</span></div>
      <p className="mb-6 mt-2 truncate text-xs text-muted-foreground" data-testid="admin-sidebar-name">{data?.name ?? user?.full_name}</p>
      <Nav perms={perms} onNavigate={() => setOpen(false)} />
      <Button testId="admin-logout-button" title="Log out" variant="outline" size="sm" icon={LogOut} className="mt-auto" onClick={logout} />
    </div>
  );
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border bg-white lg:block">{side}</aside>
      {open ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-[rgba(42,39,39,0.45)]" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">{side}</aside>
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-[#FDFBF7]/95 px-4 backdrop-blur lg:hidden">
          <button data-testid="admin-menu-button" aria-label={open ? "Close menu" : "Open menu"} onClick={() => setOpen(!open)} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-[#F4EBE6]">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Logo />
        </header>
        <main key={pathname} className="fade-up mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-8"><Outlet /></main>
      </div>
    </div>
  );
}
